import React, { useState, useEffect } from 'react';
import { Calendar, Button, Modal, DatePicker, Input, Tag, message, Spin, Empty, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faTrash, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import styled from 'styled-components';
import { COLORS } from '../../../constants/colors';
import EventTypeService from '../../../services/EventTypeService';
import BlockedDateService from '../../../services/BlockedDateService';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const SectionContainer = styled.div`
  margin-top: 24px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const SectionTitle = styled.h4`
  margin-bottom: 20px;
  color: #1a1a1a;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CalendarWrapper = styled.div`
  .ant-picker-calendar {
    background: white;
    border-radius: 12px;
    padding: 16px;
  }

  /* Make calendar cells larger */
  .ant-picker-cell {
    padding: 8px 0 !important;
    text-align: center !important;
    
    .ant-picker-cell-inner {
      width: 40px !important;
      height: 40px !important;
      font-size: 15px !important;
      border-radius: 50% !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      margin: 0 auto !important;
    }
  }

  /* Remove all selection/today borders and outlines */
  .ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-cell-inner {
    background: white !important;
    border: none !important;
    color: #000 !important;
    font-weight: 600 !important;
    box-shadow: none !important;
  }

  .ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-cell-inner::before {
    border: none !important;
  }

  .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner {
    background: transparent !important;
    color: inherit !important;
    border: none !important;
    box-shadow: none !important;
  }

  .ant-picker-cell-selected {
    background: transparent !important;
  }

  /* Remove the blue border box completely */
  .ant-picker-cell::before {
    display: none !important;
  }
  
  .ant-picker-cell-in-view.ant-picker-cell-today::before {
    border: none !important;
    display: none !important;
  }
`;

const BlockedDatesList = styled.div`
  margin-top: 16px;
  max-height: 300px;
  overflow-y: auto;
`;

const BlockedDateItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: white;
  border-radius: 6px;
  margin-bottom: 8px;
  border: 1px solid #e9ecef;

  &:hover {
    border-color: ${COLORS.$primaryBlue};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`;

const BlockedDateInfo = styled.div`
  flex: 1;
`;

const BlockedDateRange = styled.div`
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 4px;
`;

const BlockedDateReason = styled.div`
  color: #666;
  font-size: 13px;
`;

const EventTypeBlockedDatesSection = ({ eventTypeId, eventTypeName }) => {
  const [loading, setLoading] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedDates, setSelectedDates] = useState(null);
  const [reason, setReason] = useState('');
  const [calendarDate, setCalendarDate] = useState(moment());

  useEffect(() => {
    if (eventTypeId) {
      loadBlockedDates();
    }
  }, [eventTypeId]);

  const loadBlockedDates = async () => {
    if (!eventTypeId) return;
    
    try {
      setLoading(true);
      const response = await EventTypeService.getEventTypeBlockedDates(eventTypeId);
      
      console.log('🔍 [EventTypeBlockedDates] Load response:', response);
      console.log('🔍 [EventTypeBlockedDates] response.data:', response.data);
      console.log('🔍 [EventTypeBlockedDates] response.data.data:', response.data?.data);
      
      if (response.success && response.data) {
        // Backend returns { success: true, count: X, data: [...] }
        const dates = response.data.data || response.data || [];
        console.log('📅 [EventTypeBlockedDates] Setting blocked dates:', dates);
        setBlockedDates(dates);
      } else {
        console.error('❌ [EventTypeBlockedDates] Failed response:', response);
        message.error('Failed to load blocked dates');
      }
    } catch (error) {
      console.error('Error loading blocked dates:', error);
      message.error('Unable to load blocked dates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBlock = () => {
    setSelectedDates(null);
    setReason('');
    setModalVisible(true);
  };

  const handleSubmitBlock = async () => {
    if (!selectedDates || selectedDates.length !== 2) {
      message.error('Please select a date range');
      return;
    }

    if (!reason.trim()) {
      message.error('Please provide a reason');
      return;
    }

    try {
      const blockData = {
        startDate: selectedDates[0].format('YYYY-MM-DD'),
        endDate: selectedDates[1].format('YYYY-MM-DD'),
        reason: reason.trim(),
        type: 'full-day'
      };

      console.log('📤 [EventTypeBlockedDates] Creating block:', blockData);
      const response = await EventTypeService.createEventTypeBlockedDate(eventTypeId, blockData);
      console.log('📥 [EventTypeBlockedDates] Create response:', response);

      if (response.success) {
        message.success('Blocked date created successfully');
        setModalVisible(false);
        setSelectedDates(null);
        setReason('');
        await loadBlockedDates();
      } else {
        message.error(response.error || 'Failed to create blocked date');
      }
    } catch (error) {
      console.error('Error creating blocked date:', error);
      message.error('Unable to create blocked date');
    }
  };

  const handleDeleteBlock = async (blockId) => {
    Modal.confirm({
      title: 'Delete Blocked Date',
      content: 'Are you sure you want to remove this blocked date?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await BlockedDateService.deleteBlockedDate(blockId, true);
          message.success('Blocked date deleted successfully');
          loadBlockedDates();
        } catch (error) {
          console.error('Error deleting blocked date:', error);
          message.error('Unable to delete blocked date');
        }
      }
    });
  };

  const isDateBlocked = (date) => {
    if (!blockedDates || blockedDates.length === 0) {
      return false;
    }
    
    const dateStr = date.format('YYYY-MM-DD');
    
    return blockedDates.some(block => {
      const blockStartStr = moment.utc(block.startDate).format('YYYY-MM-DD');
      const blockEndStr = moment.utc(block.endDate).format('YYYY-MM-DD');
      return dateStr >= blockStartStr && dateStr <= blockEndStr;
    });
  };

  const dateFullCellRender = (date) => {
    const isBlocked = isDateBlocked(date);
    const isCurrentMonth = date.month() === calendarDate.month();
    const dateNum = date.date();
    
    return (
      <div 
        className="ant-picker-cell-inner"
        style={{
          position: 'relative',
          color: !isCurrentMonth ? '#00000040' : undefined,
          background: isBlocked ? '#fff1f0' : 'transparent',
          borderRadius: '50%'
        }}
      >
        {dateNum}
        {isBlocked && (
          <div style={{
            width: '4px',
            height: '4px',
            background: '#ff4d4f',
            borderRadius: '50%',
            position: 'absolute',
            bottom: '6px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2
          }} />
        )}
      </div>
    );
  };

  if (!eventTypeId) {
    return (
      <SectionContainer>
        <Empty description="Save the event type first to manage blocked dates" />
      </SectionContainer>
    );
  }

  return (
    <SectionContainer>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <SectionTitle>
          <FontAwesomeIcon icon={faBan} />
          Event-Specific Blocked Dates
          <Tooltip 
            title={
              <div style={{ maxWidth: '300px' }}>
                <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>Block Dates for This Event Only:</p>
                <p style={{ margin: '0 0 8px 0' }}><strong>Event-Specific:</strong> These blocked dates only apply to this event type and won&apos;t affect other event types.</p>
                <p style={{ margin: '0 0 8px 0' }}><strong>Global Blocks:</strong> Global blocked dates (set in the Blocked Dates tab) affect ALL event types.</p>
                <p style={{ margin: 0 }}><strong>Use Case:</strong> Block dates when this specific service is unavailable, such as staff vacation or equipment maintenance.</p>
              </div>
            }
            placement="right"
            overlayStyle={{ maxWidth: '350px' }}
          >
            <FontAwesomeIcon 
              icon={faQuestionCircle} 
              style={{ 
                color: '#8c8c8c', 
                cursor: 'help',
                fontSize: '14px'
              }} 
            />
          </Tooltip>
        </SectionTitle>
        <Button
          type="primary"
          icon={<FontAwesomeIcon icon={faBan} />}
          onClick={handleCreateBlock}
          style={{
            background: COLORS.$primaryBlue,
            borderColor: COLORS.$primaryBlue
          }}
        >
          Block Dates
        </Button>
      </div>

      <div style={{ color: '#666', fontSize: '13px', marginBottom: '16px' }}>
        Block specific dates for <strong>{eventTypeName}</strong> only. These blocks will not affect other event types.
        <br />
        <em>Note: Master calendar blocks (from Availability tab) will also apply to this event type.</em>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin />
        </div>
      ) : (
        <>
          <CalendarWrapper>
            <Calendar
              fullscreen={false}
              dateFullCellRender={dateFullCellRender}
              onPanelChange={setCalendarDate}
            />
          </CalendarWrapper>

          <BlockedDatesList>
            {blockedDates.length === 0 ? (
              <Empty
                description="No blocked dates for this event type"
                style={{ padding: '20px' }}
              />
            ) : (
              blockedDates.map(block => {
                const isGlobal = block.appliesTo === 'all';
                const startDate = moment.utc(block.startDate).format('MMM D, YYYY');
                const endDate = moment.utc(block.endDate).format('MMM D, YYYY');
                const dateRange = startDate === endDate ? startDate : `${startDate} - ${endDate}`;

                return (
                  <BlockedDateItem key={block._id}>
                    <BlockedDateInfo>
                      <BlockedDateRange>
                        {dateRange}
                        {isGlobal && (
                          <Tag color="orange" style={{ marginLeft: '8px' }}>
                            Global Block
                          </Tag>
                        )}
                      </BlockedDateRange>
                      <BlockedDateReason>{block.reason}</BlockedDateReason>
                    </BlockedDateInfo>
                    {!isGlobal && (
                      <Button
                        type="text"
                        danger
                        icon={<FontAwesomeIcon icon={faTrash} />}
                        onClick={() => handleDeleteBlock(block._id)}
                      />
                    )}
                  </BlockedDateItem>
                );
              })
            )}
          </BlockedDatesList>
        </>
      )}

      <Modal
        title="Block Dates"
        open={modalVisible}
        onOk={handleSubmitBlock}
        onCancel={() => setModalVisible(false)}
        okText="Block Dates"
        width={500}
      >
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
            Date Range
          </label>
          <RangePicker
            style={{ width: '100%' }}
            value={selectedDates}
            onChange={setSelectedDates}
            format="MMM D, YYYY"
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
            Reason
          </label>
          <TextArea
            rows={3}
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g., Equipment maintenance, Staff unavailable"
          />
        </div>
      </Modal>
    </SectionContainer>
  );
};

export default EventTypeBlockedDatesSection;
